/* ── Status badges ────────────────────────────────────────────────────
   Shared pill / badge components used by both PrivilegeAccessInsights
   and AccessGovernance.  Kept in their own file so neither page is the
   "owner" of something the other needs. */

import {
  PILL_BASE,
  PILL_TONES,
  REVIEW_TONES,
  reviewStatusLabel,
  type PillTone,
} from "../constants/pagConstants";
import {
  RISK_LABELS,
  RISK_TONES,
  PAM_STATUS_LABELS,
  PAM_TONES,
  type InsightsRiskLevel,
  type PamStatus,
} from "../constants/pagInsightsConstants";

/* Status pill shared by the Risk, PAM Status and Review columns - the same
   shape the assessment tables use for their status chips. */
export const StatusPill = ({ label, tone }: { label: string; tone: PillTone }) => (
  <span className={`${PILL_BASE} ${PILL_TONES[tone]}`}>{label}</span>
);

export const RiskBadge = ({ risk }: { risk: InsightsRiskLevel }) => (
  <StatusPill label={RISK_LABELS[risk]} tone={RISK_TONES[risk]} />
);

export const PamBadge = ({ status }: { status: PamStatus }) => (
  <StatusPill label={PAM_STATUS_LABELS[status]} tone={PAM_TONES[status]} />
);

export const ReviewBadge = ({
  review,
  isOverdue = false,
}: {
  review: string | null;
  isOverdue?: boolean;
}) => {
  if (!review) return <span className="text-[11px] text-muted-foreground">-</span>;
  if (isOverdue) {
    return (
      <StatusPill
        label={review === "Pending Review" ? "Overdue" : `${reviewStatusLabel(review)} (Overdue)`}
        tone="red"
      />
    );
  }
  return <StatusPill label={reviewStatusLabel(review)} tone={REVIEW_TONES[review] ?? "slate"} />;
};

/* Empty API values render as a dash rather than a blank cell. */
export const TextCell = ({ value, wrap = false }: { value: string | null | undefined; wrap?: boolean }) => (
  <span
    className={`text-sm ${wrap ? "break-words" : "whitespace-nowrap break-normal"} ${value ? "text-foreground" : "text-muted-foreground"
      }`}
  >
    {value ?? "-"}
  </span>
);
